import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Stethoscope, Users, Activity, Pill, FlaskConical, ArrowUpRight, Smartphone } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import doctorAppImg from "@/assets/12.png";
import pharmacyAppImg from "@/assets/Pharmacy.png";
import patientAppImg from "@/assets/Patient.png";
import labTechImg from "@/assets/Lab (2).png";

const APPS = [
  {
    id: "patient",
    icon: Activity,
    title: "Patient App",
    color: "from-fuchsia-500 to-violet-500",
    desc: "Your Health ID, your reports, your doctor — all in one tap. Forever.",
    bullets: ["Appointment booking", "Telemedicine consults", "Digital prescriptions", "Pay & invoice history", "Family member profiles", "AI symptom checker"],
  },
  {
    id: "doctor",
    icon: Stethoscope,
    title: "Doctor Web App",
    color: "from-rose-500 to-orange-500",
    desc: "A handheld OPD command center — consultations, e-prescriptions, lab orders, follow-ups.",
    bullets: ["Consultation dashboard", "Voice-to-Rx (AI Scribe)", "ICD-10 / SNOMED templates", "Patient timeline & vitals", "Tele-consult video", "Daily appointment queue"],
  },
  {
    id: "pharmacy",
    icon: Pill,
    title: "Pharmacy Web App",
    color: "from-amber-500 to-rose-500",
    desc: "Counter-grade billing app with prescription queue, low-stock alerts and reorder.",
    bullets: ["Prescription queue", "Barcode billing", "Inventory alerts", "Doctor & patient sync", "GST reports", "Loyalty & promos"],
  },
  {
    id: "nurse",
    icon: Users,
    title: "Nurse App",
    color: "from-emerald-500 to-cyan-500",
    desc: "Bedside-first workflow for ward management, medication administration & vitals capture.",
    bullets: ["Medication administration", "Ward rounds checklist", "Real-time vitals entry", "Care plan tasks", "Shift handover notes", "OT preparation"],
  },
  {
    id: "lab-tech",
    icon: FlaskConical,
    title: "Lab Technician Web App",
    color: "from-cyan-500 to-sky-600",
    desc: "Sample-to-report mobile workflow — fast, accurate, audit-ready.",
    bullets: ["Barcode sample scan", "Sample tracking", "Result upload", "Quality control flags", "Home collection routes", "Daily TAT dashboard"],
  },
];

function PhoneMock({ app }) {
  return (
    <div className="relative mx-auto w-[220px] h-[440px]">
      <div className="absolute inset-0 rounded-[40px] bg-slate-900 shadow-2xl" />
      <div className="absolute inset-[6px] rounded-[34px] bg-white overflow-hidden z-0" style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
        <div className={`absolute -top-20 -right-16 w-64 h-64 rounded-full bg-gradient-to-br ${app.color} opacity-90 blur-xl`} />
        <div className="absolute inset-0 p-5 flex flex-col">
          <div className="flex justify-between text-[10px] text-slate-500 font-medium">
            <span>9:41</span>
            <span>•••</span>
          </div>
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold">SAALUVARS</div>
            <div className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Outfit',sans-serif" }}>{app.title}</div>
          </div>
          <div className="mt-5 space-y-2.5">
            {app.bullets.slice(0, 4).map((b, i) => (
              <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-[11px] font-medium text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--brand)]" />
                {b}
              </div>
            ))}
          </div>
          <div className="mt-auto">
            <div className="rounded-xl bg-slate-900 text-white text-xs font-bold p-3 text-center">
              Open {app.title.split(" ")[0]}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-slate-900" />
    </div>
  );
}

export default function Apps() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace("#", ""));
      if (element) {
        // Wait briefly for elements to render/framer-motion to initialize
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [hash]);

  return (
    <div data-testid="page-apps">
      <section className="relative bg-[color:var(--paper-2)] overflow-hidden">
        <div className="absolute inset-0 bg-grid-light opacity-40" />
        <div className="relative container-x pt-4 md:pt-6 lg:pt-8 pb-12">
          <SectionHeader
            eyebrow="Native Apps"
            title="5 apps. One platform."
            titleAccent="Zero context switching."
            subtitle="Every role gets a purpose-built mobile experience — connected by a single Saaluvars Health ID."
          />
        </div>
      </section>

      <section className="section-y bg-white overflow-hidden">
        <div className="container-x">
          {APPS.map((app, i) => (
            <motion.div
              key={app.id}
              id={app.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center scroll-mt-28 ${i === 0 ? '' : (i === 1 || i === 2) ? 'mt-24 lg:mt-48' : 'mt-32 lg:mt-64'}`}
              data-testid={`app-${app.id}`}
            >
              <div className={`lg:col-span-5 relative flex justify-center items-center order-1 ${i % 2 !== 0 ? "lg:order-2" : "lg:order-1"}`}>
                {!['patient', 'doctor', 'pharmacy', 'lab-tech'].includes(app.id) && (
                  <>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[100%] md:-translate-y-[80%] lg:-translate-y-[60%] pointer-events-none z-0 min-w-max">
                      <div className="text-[48px] sm:text-[64px] md:text-[80px] lg:text-[90px] xl:text-[100px] font-black text-slate-900 opacity-20 leading-[0.8] text-center tracking-tighter select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Coming
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[80%] md:translate-y-[60%] lg:translate-y-[40%] pointer-events-none z-0 min-w-max">
                      <div className="text-[48px] sm:text-[64px] md:text-[80px] lg:text-[90px] xl:text-[100px] font-black text-slate-900 opacity-20 leading-[0.8] text-center tracking-tighter select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Soon
                      </div>
                    </div>
                  </>
                )}
                {['lab-tech'].includes(app.id) && (
                  <>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[70%] pointer-events-none z-0 min-w-max">
                      <div className="text-[48px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-black text-slate-900 opacity-20 leading-[0.8] text-center tracking-tighter select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Coming
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/3 pointer-events-none z-0 min-w-max">
                      <div className="text-[48px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-black text-slate-900 opacity-20 leading-[0.8] text-center tracking-tighter select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Soon
                      </div>
                    </div>
                  </>
                )}
                <div className="relative z-10 w-full">
                  {app.id === 'doctor' ? (
                    <img src={doctorAppImg} alt="Doctor App Dashboard" className="w-full lg:w-[110%] xl:w-full mx-auto h-auto drop-shadow-2xl" draggable="false" />
                  ) : app.id === 'pharmacy' ? (
                    <img src={pharmacyAppImg} alt="Pharmacy App Dashboard" className="w-full lg:w-[110%] xl:w-full mx-auto h-auto drop-shadow-2xl" draggable="false" />
                  ) : app.id === 'lab-tech' ? (
                    <img src={labTechImg} alt="Lab Technician App Dashboard" className="w-full lg:w-[110%] xl:w-full mx-auto h-auto drop-shadow-2xl" draggable="false" />
                  ) : app.id === 'patient' ? (
                    <img src={patientAppImg} alt="Patient App Dashboard" className="max-w-[200px] md:max-w-[220px] mx-auto h-auto drop-shadow-2xl rounded-[32px]" draggable="false" />
                  ) : (
                    <PhoneMock app={app} />
                  )}
                </div>
              </div>
              <div className={`lg:col-span-7 lg:pl-6 xl:pl-10 order-2 ${i % 2 !== 0 ? "lg:order-1" : "lg:order-2"}`}>
                {!['patient', 'doctor', 'pharmacy', 'lab-tech'].includes(app.id) && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${app.color} text-white text-xs font-bold tracking-wider uppercase`}>
                    <app.icon className="w-3.5 h-3.5" /> Coming Soon
                  </div>
                )}
                <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>{app.title}</h2>
                <p className="mt-3 text-lg text-slate-600 max-w-xl">{app.desc}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {app.bullets.map(b => (
                    <div key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <Smartphone className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                      {b} 
                    </div> 
                  ))} 
                </div>
                <div className="mt-7 flex gap-3">
                  {/*
                  <Link to="/request-demo" className="btn-primary">
                    {['doctor', 'pharmacy', 'lab-tech'].includes(app.id) ? 'Book Demo' : `Try ${app.title.split(" ")[0]}`} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                  */}
                  <Link to="/pricing" className="btn-ghost">View Plans</Link>
                  <Link to="/contact" className="btn-primary">Contact <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}